import { Dialog, Menu, MenuItem } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { HiOutlineSparkles } from "react-icons/hi";
import { LuArrowRight, LuCheck, LuX } from "react-icons/lu";
import { useHistory } from "react-router-dom";
import { GUIDES, isGuideDone, requestGuide } from "./guides";

const itemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 12,
  minHeight: 48,
  color: "#374151",
  padding: "5px 12px",
};

// "Guide Me" button: lists every guide so a user can replay any of them manually.
export const GuideMenu = () => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const entries = Object.entries(GUIDES);
  const doneCount = entries.filter(([key]) => isGuideDone(key)).length;
  const nextKey = entries.find(([key]) => !isGuideDone(key))?.[0];

  return (
    <>
      <button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{ height: 32, minHeight: 32, maxHeight: 32, fontSize: 12, borderRadius: 6, fontWeight: 500 }}
        className="flex items-center gap-1.5 px-3 font-medium text-[#313C4C] bg-[#313C4C]/10 hover:bg-[#313C4C]/20 transition-all duration-200 whitespace-nowrap"
      >
        <HiOutlineSparkles className="w-3.5 h-3.5" />
        Guide Me
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: { borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", minWidth: 320, maxWidth: 340, padding: 0 },
        }}
        MenuListProps={{ style: { padding: 0 } }}
      >
        <div className="px-3 py-2 border-b border-slate-200 bg-[#f8fafc]">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Getting started</div>
          <div className="text-[11px] text-slate-600 mt-0.5">{doneCount} of {entries.length} done</div>
          <div className="h-1 mt-1.5 bg-slate-200">
            <div className="h-1 bg-[#313C4C]" style={{ width: `${(doneCount / entries.length) * 100}%` }} />
          </div>
        </div>
        {entries.map(([key, guide], i) => {
          const done = isGuideDone(key);
          return (
            <MenuItem
              key={key}
              style={{ ...itemStyle, fontWeight: key === nextKey ? 600 : 500, color: done ? "#64748b" : "#0f172a" }}
              onClick={() => {
                setAnchorEl(null);
                requestGuide(key, history);
              }}
            >
              <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full border border-slate-300 text-[10px] text-slate-500">
                {done ? <LuCheck size={12} color="#313C4C" /> : i + 1}
              </span>
              <span className="flex-1 whitespace-normal">
                <span className="block text-[12px]">{guide.label}</span>
                <span className="block text-[11px] font-medium text-slate-500">{guide.desc}</span>
              </span>
              <span className="text-[11px] font-medium text-slate-500 flex-shrink-0">{done ? "Replay" : key === nextKey ? "Start" : ""}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// Shown when a guide ends: offers the next stop of the flow. Never redirects on its own.
// guideKey: start that guide (goes to its page); path: just go there.
export const GuideContinueCard = ({ title, text, label, guideKey, path, onClose, modal = false }) => {
  const history = useHistory();
  const [toastShown, setToastShown] = useState(() => !!document.querySelector(".Toastify__toast"));
  useEffect(() => {
    if (modal) return undefined;
    const timer = setInterval(() => setToastShown(!!document.querySelector(".Toastify__toast")), 300);
    return () => clearInterval(timer);
  }, [modal]);

  const go = () => {
    onClose();
    if (guideKey) requestGuide(guideKey, path ? null : history);
    if (path) history.push(path);
  };

  const entries = Object.keys(GUIDES);
  const doneCount = entries.filter((key) => isGuideDone(key)).length;

  const content = (
    <div className={`w-80 bg-white overflow-hidden ${modal ? "" : "fixed bottom-4 right-4 z-[1200] border border-slate-200 rounded-xl shadow-lg"}`}>
      <div className="h-1 bg-slate-100">
        <div className="h-1 bg-[#313C4C] transition-all duration-200" style={{ width: `${(doneCount / entries.length) * 100}%` }} />
      </div>
      <div className="flex gap-3 p-3">
        <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-slate-100 text-[#313C4C]">
          <HiOutlineSparkles size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Next step · {doneCount} of {entries.length} done
              </p>
              <p className="text-[13px] font-semibold text-slate-900 mt-0.5 leading-tight">{title}</p>
            </div>
            <button onClick={onClose} aria-label="Dismiss" className="p-1 -mt-1 -mr-1 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors cursor-pointer">
              <LuX size={14} />
            </button>
          </div>
          <p className="text-[11px] text-slate-600 mt-1">{text}</p>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={onClose}
              className="h-7 px-3 text-[11px] font-normal text-slate-900 bg-white hover:bg-slate-100 border border-slate-300 rounded-none flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
            >
              Later
            </button>
            <button
              onClick={go}
              className="h-7 px-3 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-normal rounded-none flex items-center justify-center gap-1.5 shadow-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              {label}
              <LuArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  if (modal) return <Dialog open onClose={onClose} aria-label={title}>{content}</Dialog>;
  return toastShown ? null : content;
};
